import { useMemo } from 'react';
import { Link as ReactLink } from 'react-router-dom';

import { useCustomerInfo } from '@/api/customers';
import { useSubscriptionsInfo, useUpcomingInvoice } from '@/api/subscriptions';
import { AlertChip } from '@/components/AlertChip';
import { DisplayCurrency } from '@/components/DisplayCurrency';
import {
    SummaryCard,
    SummaryCardBody,
    SummaryCardButton,
    SummaryCardProps,
    SummaryCardSubtext,
} from '@/components/ui/SummaryCard';
import { useBackTargetForCurrentPage } from '@/context/transaction/useBackTargetForCurrentPage';
import { errorMessage } from '@/utils';

export type AccountBalanceCardProps = {
    sx?: SummaryCardProps['sx'];
};

export const accountBalanceCardSubtext = (customerBalanceCents: number, nextInvoiceTotalCents: number | undefined) => {
    if (customerBalanceCents <= 0 || !nextInvoiceTotalCents || nextInvoiceTotalCents <= 0) {
        return '';
    }

    const nMonthsFullyCovered = Math.floor(customerBalanceCents / nextInvoiceTotalCents);
    if (nMonthsFullyCovered === 0) {
        return 'Insufficient for next invoice.';
    }
    if (nMonthsFullyCovered === 1) {
        return 'Estimated to fully pay next recurring invoice.';
    }

    return `Estimated to fully pay next ${nMonthsFullyCovered} recurring invoices.`;
};

export const AccountBalanceCard = ({ ...props }: AccountBalanceCardProps) => {
    const { data: customerInfo, isError, error, isPending } = useCustomerInfo();
    const { data: subscriptionInfo } = useSubscriptionsInfo();
    const { data: upcomingInvoice } = useUpcomingInvoice({
        subscriptionId: subscriptionInfo?.data?.[0]?.id,
    });
    const transactionBackTarget = useBackTargetForCurrentPage();

    const subtext = useMemo(() => {
        const customerBalanceCents = -(customerInfo?.data?.balance ?? 0);
        const nextInvoiceTotalCents = upcomingInvoice?.data?.total;
        return accountBalanceCardSubtext(customerBalanceCents, nextInvoiceTotalCents);
    }, [customerInfo?.data, upcomingInvoice?.data]);

    return (
        <SummaryCard title="Account balance" isLoading={isPending} {...props} data-testid="account-balance-content">
            <SummaryCardBody>
                {isError ? (
                    <AlertChip severity="error" label={errorMessage(error, 'Unable to load account balance')} />
                ) : (
                    <DisplayCurrency
                        value={customerInfo?.data?.balance}
                        currency={customerInfo?.data?.currency}
                        negative
                        cents
                    />
                )}
            </SummaryCardBody>
            <SummaryCardSubtext>{subtext}</SummaryCardSubtext>
            <SummaryCardButton component={ReactLink} to="/payment/accountBalance" state={{ transactionBackTarget }}>
                Add funds to balance
            </SummaryCardButton>
        </SummaryCard>
    );
};
